import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type ResponseType,
} from 'axios';
import env from 'env';
import type { CandidateContext } from 'candidate-app/candidateContext';

import { getAppTransport } from './appTransport';
import { refreshTeamsSsoToken } from './teamsSsoSession';
import { readHeader, setHeader } from './httpHeaders';
import { redactAxiosRequestError } from './requestErrorMetadata';
export { setHeader } from './httpHeaders';
export {
  hasHeader,
  SHARE_CAPABILITY_HEADER,
  DEACTIVATED_ACCOUNT_CODE,
  LOGIN_REASON_PARAM,
  LOGIN_REASON_DEACTIVATED,
} from './authFailure';

// Axios treats 0 as "wait forever". A stalled mobile socket then leaves
// CallRuntime awaiting finalizeRecording and call completion status, and
// the same instance serves Persona verify, frame uploads, and the token
// fetch. 90s fails that hang before the ~100s proxy ceiling; per-request
// timeouts still override this default.
export const DEFAULT_AXIOS_TIMEOUT_MS = 90_000;

const instance = axios.create({
  baseURL: env.REACT_APP_SERVER_URL,
  paramsSerializer: { indexes: null },
  timeout: DEFAULT_AXIOS_TIMEOUT_MS,
});

// Capture policy once: retries and responses belong to the app that sent the request.
instance.interceptors.request.use(
  (config) => {
    const policy = config.appTransportPolicy ?? getAppTransport();
    config.appTransportPolicy = policy;
    const prepare = () => {
      for (const [key, value] of Object.entries(policy.getAuthHeaders())) {
        if (value) setHeader(config, key, value);
      }
      return policy.prepareRequest(config);
    };
    const refresh = refreshTeamsSsoToken();
    return refresh ? refresh.then(prepare) : prepare();
  },
  (error) => Promise.reject(error)
);

/**
 * True when the response's content-type media type is `text/html`, e.g. an
 * SPA fallback page or proxy error page served where the API was expected.
 */
export function isHtmlResponse(headers: unknown): boolean {
  const contentType = readHeader(headers, 'content-type');
  if (!contentType) return false;
  const mediaType = contentType.split(';')[0] ?? '';
  return mediaType.trim().toLowerCase() === 'text/html';
}

// Rejects an HTML body on a JSON call (a proxy, captive-portal or SSO page served
// in place of the API), then handles auth failures.
instance.interceptors.response.use(
  (response) => {
    if (!response.config.responseType && isHtmlResponse(response.headers)) {
      throw new AxiosError(
        'Expected a JSON API response but received HTML',
        AxiosError.ERR_BAD_RESPONSE,
        response.config,
        response.request,
        response
      );
    }
    return response;
  },
  (error: AxiosError<{ loginRequired?: boolean; detailCode?: string }>) => {
    const policy = error.config?.appTransportPolicy ?? getAppTransport();
    const capabilityRetry = policy.retryRequest(error, (config) =>
      instance.request<unknown>(config)
    );
    if (capabilityRetry) return capabilityRetry;

    policy.handleAuthFailure(error.response, error.config?.headers);

    return Promise.reject(error);
  }
);

// Runs after retry/auth policy and the HTML response guard. Keep the original
// error kind and response data, but never hand request credentials to callers
// that may log the error (including nested response.config and browser XHR).
instance.interceptors.response.use(undefined, (error: unknown) =>
  Promise.reject(redactAxiosRequestError(error))
);

/**
 * Second argument to Orval-generated API functions (via the axios mutator).
 * Orval threads it into every generated function, enabling per-request config
 * (timeout, signal, etc.) without duplicating the generated FormData/JSON
 * boilerplate in callers.
 */
export type OrvalRequestOptions = {
  adapter?: AxiosRequestConfig['adapter'];
  candidateContext?: CandidateContext | null;
  timeout?: number;
  signal?: AbortSignal;
  responseType?: ResponseType;
  // Call-site headers augment the generated config headers.
  headers?: Record<string, string>;
  // Opt-in for cross-site cookie flows (e.g. Zoom OAuth state cookies).
  withCredentials?: boolean;
};

/**
 * Orval mutator. `T` is the server's declared response model, kept in sync with
 * the backend by `server/bin/check-endpoint-response-models` and the `api-client`
 * CI drift job; the body is not validated against it at runtime. Call sites that
 * persist or branch on a payload's shape still parse it with `utils/typeGuards`.
 * The response interceptor rejects an HTML body on a JSON call, since no `T` can
 * match it.
 */
export const axiosInstance = <T>(
  config: {
    url: string;
    method: string;
    params?: unknown;
    data?: unknown;
    headers?: Record<string, string>;
    signal?: AbortSignal;
    // Orval sets this for endpoints whose spec declares a binary body.
    responseType?: ResponseType;
  },
  options?: OrvalRequestOptions
): Promise<T> => {
  return instance
    .request<T>({
      ...config,
      ...options,
      headers: { ...config.headers, ...options?.headers },
    })
    .then((response) => response.data);
};

export default instance;
